import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSurfaceLayer } from '../lib/useDismiss'
import { useHubLang } from '../lib/i18n'
import { TOUR } from '../lib/hubCopy'
import { TOUR_STEPS, findTarget, placeCard, type Box } from '../lib/tour'

/**
 * The hub tour — SHL-34. One card per stop, a spotlight on what it describes.
 *
 * What it points at, and why that survives two layouts, is lib/tour.ts. This
 * file is the furniture: the spotlight, the card, the keys.
 *
 * ── A DIALOG IN WHAT IT PROMISES, NOT IN HOW IT IS BUILT ────────────────────
 *
 * The card is `role="dialog" aria-modal="true"` and keeps the promise Dialog.tsx
 * exists to keep: focus moves to it, Tab stays inside it, Escape closes it
 * (through the surface stack), and focus goes back where it was. It is not a
 * <Dialog> because a dialog locks the page and centres a panel over a scrim,
 * and a tour has to do neither — the page behind is the subject, and the card
 * sits beside the thing it is about.
 *
 * ── THE SCRIM DOES NOT CLOSE IT ─────────────────────────────────────────────
 *
 * Closing the tour records it as seen (158), so a stray tap on the dimmed page
 * would end it for good on the first screen. "Skip the tour" and Escape are the
 * ways out, and both are deliberate. The scrim also swallows presses on the
 * page, so a tap on the highlighted control does not navigate away mid-tour.
 *
 * ── PORTAL IN THE BROWSER, IN PLACE UNDER renderToString ────────────────────
 *
 * Dialog's reason (29 Sep): `.hub__side` is sticky and forms a stacking
 * context, so anything fixed rendered inside the shell can be painted under
 * the hubbar. `createPortal` throws without a document, so the suite renders
 * it in place — and with no document there is no target, so it renders the
 * first stop centred, which is what the suite asserts on.
 */
export function Tour({
  open,
  onClose,
}: {
  open: boolean
  /** `finished` is true for Done, false for Skip or Escape. Both record it. */
  onClose: (finished: boolean) => void
}) {
  const t = TOUR[useHubLang()]
  const [index, setIndex] = useState(0)
  const [target, setTarget] = useState<Box | null>(null)
  const [cardSize, setCardSize] = useState({ width: 352, height: 200 })
  const cardRef = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)
  const isTopmost = useSurfaceLayer(open)
  const titleId = useId()
  const bodyId = useId()
  const latestClose = useRef(onClose)
  latestClose.current = onClose

  const step = TOUR_STEPS[index]
  const last = index === TOUR_STEPS.length - 1

  // Every opening starts at the first stop.
  useEffect(() => {
    if (open) setIndex(0)
  }, [open])

  // Focus in on open, back out on close — Dialog's half that people notice
  // only when it is missing.
  useEffect(() => {
    if (!open) return
    returnTo.current = document.activeElement as HTMLElement | null
    cardRef.current?.focus()
    function onDocKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape' || !isTopmost()) return
      if (cardRef.current?.contains(document.activeElement)) return
      latestClose.current(false)
    }
    document.addEventListener('keydown', onDocKeyDown)
    return () => {
      document.removeEventListener('keydown', onDocKeyDown)
      if (returnTo.current?.isConnected) returnTo.current.focus()
    }
  }, [open, isTopmost])

  /**
   * Where the stop's target is NOW. Re-measured on scroll and resize, and on a
   * short timer, because the hub's cards arrive after the shell does: One
   * Question and the contest card are fetched, so the first measure can find
   * nothing and the second find the card. Only a CHANGED box re-renders.
   */
  const measure = useCallback(() => {
    if (!open || !step) return
    const el = findTarget(step, document)
    const next = el ? toBox(el.getBoundingClientRect()) : null
    setTarget((was) => (sameBox(was, next) ? was : next))
    const c = cardRef.current?.getBoundingClientRect()
    if (c) setCardSize((was) => (Math.abs(was.width - c.width) < 1 && Math.abs(was.height - c.height) < 1 ? was : { width: c.width, height: c.height }))
  }, [open, step])

  // A new stop: bring its target into view, then measure.
  useEffect(() => {
    if (!open || !step) return
    const el = findTarget(step, document)
    // VERTICALLY ONLY, and never for something pinned to the viewport (the
    // tab bar, the bubble, the topbar): scrollIntoView also scrolls sideways,
    // and on a phone it slid the whole hub left to reach the initials.
    const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    // SIDEWAYS ONLY INSIDE THE HUB'S SWIPE ROW (11 Oct). Below 1100px One
    // Question and the contest are tiles in `.hub__hooks`, and the contest is
    // the second tile — half off screen. The row is scrolled, never the page,
    // which is the sideways slide the comment above is guarding against.
    const row = el?.closest<HTMLElement>('.hub__hooks')
    if (el && row && row.scrollWidth > row.clientWidth) {
      const pad = parseFloat(getComputedStyle(row).paddingLeft || '0')
      const left = row.scrollLeft + el.getBoundingClientRect().left - row.getBoundingClientRect().left - pad
      row.scrollTo({ left: Math.max(0, left), behavior: reduced ? 'auto' : 'smooth' })
    }
    if (el && !inFixedLayer(el) && !inView(el.getBoundingClientRect())) {
      const r = el.getBoundingClientRect()
      window.scrollTo({
        top: Math.max(0, window.scrollY + r.top - Math.max(0, (window.innerHeight - r.height) / 2)),
        behavior: reduced ? 'auto' : 'smooth',
      })
    }
    measure()
  }, [open, step, measure])

  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    const timer = window.setInterval(measure, 300)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
      window.clearInterval(timer)
    }
  }, [open, measure])

  if (!open || !step) return null

  const copy = t.steps[step.key]
  const view =
    typeof window === 'undefined' ? { width: 1024, height: 768 } : { width: window.innerWidth, height: window.innerHeight }
  // Padded, then CLAMPED to the screen: the tab bar sits on the bottom edge
  // and the initials on the right one, and a ring drawn past the edge is a
  // ring with a side missing.
  const hole = target ? padInside(target, view, 6, 3) : null
  const at = placeCard(hole, view, cardSize)

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      if (!isTopmost()) return
      e.stopPropagation()
      onClose(false)
      return
    }
    if (e.key === 'ArrowRight' && !last) setIndex((i) => i + 1)
    if (e.key === 'ArrowLeft' && index > 0) setIndex((i) => i - 1)
    if (e.key !== 'Tab') return
    // Tab stays on the card: a modal you can tab out of is one the page
    // behind is still reachable through.
    const items = Array.from(cardRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])
    if (items.length === 0) return e.preventDefault()
    const i = items.indexOf(document.activeElement as HTMLElement)
    if (e.shiftKey && i <= 0) {
      e.preventDefault()
      items[items.length - 1].focus()
    } else if (!e.shiftKey && i === items.length - 1) {
      e.preventDefault()
      items[0].focus()
    }
  }

  const node = (
    <div className="tour" role="presentation" onKeyDown={onKeyDown}>
      {/* The page is dimmed by the spotlight's own shadow when there is a
          target, and by a plain scrim when there is not. Either way the layer
          takes the presses (see the header). */}
      <div className={hole ? 'tour__catch' : 'tour__catch tour__catch--scrim'} aria-hidden="true" />
      {hole && (
        <div
          className="tour__spot"
          aria-hidden="true"
          style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
        />
      )}
      <div
        ref={cardRef}
        className={`tour__card tour__card--${at.side}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        data-step={step.key}
        style={{ top: at.top, left: at.left }}
      >
        <p className="tour__eyebrow">
          {t.label} · {t.progress(index + 1, TOUR_STEPS.length)}
        </p>
        {/* Live, so a screen reader hears the next stop when Next keeps focus. */}
        <div aria-live="polite">
          <h2 className="tour__title" id={titleId}>
            {copy.title}
          </h2>
          <p className="tour__body" id={bodyId}>
            {copy.body}
          </p>
        </div>
        <div className="tour__foot">
          {!last && (
            <button type="button" className="btn btn--quiet btn--sm tour__skip" onClick={() => onClose(false)}>
              {t.skip}
            </button>
          )}
          <span className="tour__nav">
            {index > 0 && (
              <button type="button" className="btn btn--quiet btn--sm" onClick={() => setIndex((i) => i - 1)}>
                {t.back}
              </button>
            )}
            {last ? (
              <button type="button" className="btn btn--primary btn--sm" onClick={() => onClose(true)}>
                {t.done}
              </button>
            ) : (
              <button type="button" className="btn btn--primary btn--sm" onClick={() => setIndex((i) => i + 1)}>
                {t.next}
              </button>
            )}
          </span>
        </div>
      </div>
    </div>
  )
  return typeof document === 'undefined' ? node : createPortal(node, document.body)
}

function toBox(r: DOMRect): Box {
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

function sameBox(a: Box | null, b: Box | null): boolean {
  if (a === null || b === null) return a === b
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  )
}

function padInside(t: Box, view: { width: number; height: number }, pad: number, edge: number): Box {
  const top = Math.max(edge, t.top - pad)
  const left = Math.max(edge, t.left - pad)
  const bottom = Math.min(view.height - edge, t.top + t.height + pad)
  const right = Math.min(view.width - edge, t.left + t.width + pad)
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) }
}

/** Is this element, or anything above it, pinned to the viewport? */
function inFixedLayer(el: Element): boolean {
  for (let n: Element | null = el; n && n !== document.body; n = n.parentElement) {
    const p = getComputedStyle(n).position
    if (p === 'fixed' || p === 'sticky') return true
  }
  return false
}

/** Fully inside the viewport, with room for the card beside it. */
function inView(r: DOMRect): boolean {
  return r.top >= 64 && r.bottom <= window.innerHeight - 96 && r.left >= 0 && r.right <= window.innerWidth
}
